import React, { useEffect, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import Link from '@amzn/awsui-components-core/link';
import { isNavigableUrl } from '../../utils/safeUrl';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './ContentTable.scss';

type ColumnIndex = 1 | 2 | 3 | 4 | 5 | 6;

interface ResolvedColumn {
  index: ColumnIndex;
  header?: string;
  cells: string[];
}

/**
 * Cells carry inline markdown links out to longer-form content. Anchors render as
 * body-copy links and the wrapping paragraph is unwrapped so a cell stays a single
 * table-cell line. A link whose scheme is not navigable degrades to its own text:
 * emitting the anchor with an empty href would leave a focusable control that
 * navigates to the current page and is announced as a link with no destination.
 */
const CELL_MARKDOWN_COMPONENTS = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
    isNavigableUrl(href) ? (
      <Link href={href as string} variant="secondary" fontSize="inherit">
        {children}
      </Link>
    ) : (
      <>{children}</>
    ),
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

/**
 * Cell content is prose plus short lists, so the allow-list admits inline formatting,
 * links and list markup. A fee or condition cell commonly enumerates several items, and
 * a `<ul>` inside a `<td>` is ordinary table content.
 *
 * Everything else — headings, images, blockquotes, code blocks — is unwrapped to its text:
 * those establish page-level structure that would break the table's own layout, and a cell
 * is not the place to introduce it. GFM is deliberately not enabled: its extensions are
 * block-level (tables, task lists) and a table nested in a cell is the shape this block
 * exists to replace.
 */
const CELL_ALLOWED_ELEMENTS = ['p', 'a', 'strong', 'em', 'code', 'ul', 'ol', 'li'];

/**
 * A framing note is a single caveat sentence rendered in a `<p>`, so it takes the cell
 * allow-list minus the list markup. `CellText` unwraps the markdown paragraph, which would put a
 * bare `<ul>` directly inside that `<p>` — invalid markup that a browser resolves by closing the
 * paragraph early, detaching the list from the note's own styling. Lists are a cell affordance
 * for enumerating conditions against a row; a note has no row to enumerate against.
 */
const NOTE_ALLOWED_ELEMENTS = ['p', 'a', 'strong', 'em', 'code'];

const CellText: React.FC<{ children?: string; allowedElements?: string[] }> = ({
  children,
  allowedElements = CELL_ALLOWED_ELEMENTS,
}) => (
  <ReactMarkdown
    components={CELL_MARKDOWN_COMPONENTS}
    allowedElements={allowedElements}
    unwrapDisallowed
  >
    {children ?? ''}
  </ReactMarkdown>
);

/** Per-column flat props for CMS authoring (columns 1-6). */
export interface ContentTableColumnProps {
  /**
   * Column 1 heading.
   * @polyHelp Heading for this column, shown in the table's header row. A column with cells but no heading still renders, but screen readers announce it as "Column 1" in English in every locale — so name it unless the column genuinely has no name.
   */
  column1Header?: string;
  /**
   * Column 1 cells, one translatable string per row, aligned by index to `rowHeaders`.
   * @polyType List
   * @polyLabel Column 1 cells
   * @polyHelp One entry per row, in the same order as Row labels. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here. Each cell is translated as its own unit.
   * @polyTranslatable true
   */
  column1?: string[];

  /**
   * Column 2 heading.
   * @polyHelp Heading for this column, shown in the table's header row. A column with cells but no heading still renders, but screen readers announce it as "Column 2" in English in every locale — so name it unless the column genuinely has no name.
   */
  column2Header?: string;
  /**
   * Column 2 cells, one translatable string per row, aligned by index to `rowHeaders`.
   * @polyType List
   * @polyLabel Column 2 cells
   * @polyHelp One entry per row, in the same order as Row labels. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here. Each cell is translated as its own unit.
   * @polyTranslatable true
   */
  column2?: string[];

  /**
   * Column 3 heading.
   * @polyHelp Heading for this column, shown in the table's header row. A column with cells but no heading still renders, but screen readers announce it as "Column 3" in English in every locale — so name it unless the column genuinely has no name.
   */
  column3Header?: string;
  /**
   * Column 3 cells, one translatable string per row, aligned by index to `rowHeaders`.
   * @polyType List
   * @polyLabel Column 3 cells
   * @polyHelp One entry per row, in the same order as Row labels. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here. Each cell is translated as its own unit.
   * @polyTranslatable true
   */
  column3?: string[];

  /**
   * Column 4 heading.
   * @polyHelp Heading for this column, shown in the table's header row. A column with cells but no heading still renders, but screen readers announce it as "Column 4" in English in every locale — so name it unless the column genuinely has no name.
   */
  column4Header?: string;
  /**
   * Column 4 cells, one translatable string per row, aligned by index to `rowHeaders`.
   * @polyType List
   * @polyLabel Column 4 cells
   * @polyHelp One entry per row, in the same order as Row labels. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here. Each cell is translated as its own unit.
   * @polyTranslatable true
   */
  column4?: string[];

  /**
   * Column 5 heading.
   * @polyHelp Heading for this column, shown in the table's header row. A column with cells but no heading still renders, but screen readers announce it as "Column 5" in English in every locale — so name it unless the column genuinely has no name.
   */
  column5Header?: string;
  /**
   * Column 5 cells, one translatable string per row, aligned by index to `rowHeaders`.
   * @polyType List
   * @polyLabel Column 5 cells
   * @polyHelp One entry per row, in the same order as Row labels. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here. Each cell is translated as its own unit.
   * @polyTranslatable true
   */
  column5?: string[];

  /**
   * Column 6 heading.
   * @polyHelp Heading for this column, shown in the table's header row. A column with cells but no heading still renders, but screen readers announce it as "Column 6" in English in every locale — so name it unless the column genuinely has no name.
   */
  column6Header?: string;
  /**
   * Column 6 cells, one translatable string per row, aligned by index to `rowHeaders`.
   * @polyType List
   * @polyLabel Column 6 cells
   * @polyHelp One entry per row, in the same order as Row labels. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here. Each cell is translated as its own unit.
   * @polyTranslatable true
   */
  column6?: string[];
}

/**
 * Columns are authored as scalar `string[]` lists rather than an array of row objects so
 * that every cell is its own translatable unit — the property Poly cannot express with an
 * object-array prop, and the reason the column ceiling is fixed rather than dynamic.
 *
 * @polySemanticUsage About this block: a table for comparing options or listing rates.
 * Use it for plan and tier comparisons, fee and rate schedules, and feature matrices — any
 * content where a left-hand column names the attribute and each further column is one
 * option, period, or category. The row-label column is optional, so a headers-plus-columns
 * grid works too. Supports up to six columns; prefer two or three for a side-by-side
 * comparison and more only for a genuine rate schedule. Cell text and row labels support
 * inline markdown links for pointing at longer-form content — do not put buttons in a
 * table. A cell may also hold a short bulleted list, written as one `- item` per line
 * separated by newline characters, for a source cell that enumerates several conditions or
 * qualifiers; the newlines are what make it a list, and a single line of dash-separated
 * text renders as one run-together bullet. Keep list items to a few words and prefer
 * separate rows when each item has its own value. Headings, images, blockquotes and code
 * blocks are stripped to plain text in a cell. Use `topNote` and `bottomNote` for scope
 * caveats and legal or fee context that applies to the whole table, and `stickyHeader`
 * only for long schedules that scroll.
 */
export interface ContentTableProps extends ContentTableColumnProps {
  /**
   * Optional heading shown above the table.
   * @polyType Text
   * @polyLabel Title
   */
  title?: string;

  /**
   * Optional intro paragraph shown between the title and the table.
   * @polyType RichText
   * @polyLabel Description
   * @polyHelp Intro paragraph shown above the table. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path) — a good place to link the full schedule this table summarises.
   */
  description?: string;

  /**
   * Left-hand attribute-label column. One translatable string per row; row `i` pairs
   * `rowHeaders[i]` with each column's `columnN[i]`.
   * @polyType List
   * @polyLabel Row labels
   * @polyHelp Enter one label per row, in the same top-to-bottom order as the cells. To leave a row unlabelled, press Enter with the field empty: a (blank) entry holds that row's place so the labels below it stay on their own rows. Skipping a row instead of adding a blank shifts every label below it onto the wrong row. Inline formatting works: **bold**, *italic*, `code`, and [link text](/path). This field is single-line, so a bulleted list cannot be typed here.
   * @polyTranslatable true
   * @polyAllowBlank true
   */
  rowHeaders?: string[];

  /**
   * Optional heading for the row-label column (the top-left corner cell).
   * @polyType Text
   * @polyLabel Row-label heading
   * @polyHelp Heading for the row-label column, shown in the top-left corner cell. Safe to leave empty — the corner cell is usually blank when the row labels already describe themselves.
   */
  rowHeadersLabel?: string;

  /**
   * Framing note shown above the table (e.g. a scope caveat).
   * @polyType Text
   * @polyLabel Top note
   */
  topNote?: string;

  /**
   * Framing note shown below the table (e.g. "All fees exclude VAT.").
   * @polyType Text
   * @polyLabel Bottom note
   */
  bottomNote?: string;

  /**
   * Keep the header row pinned while a long table body scrolls.
   * @polyType Boolean
   * @polyLabel Sticky header
   * @polyHelp Pins the header row so column names stay visible. This also caps the table's height (about 640px) and gives the body its own vertical scrollbar, so the whole table no longer grows down the page. Use it for long rate schedules; leave it off for short tables.
   */
  stickyHeader?: boolean;

  /**
   * Shade alternating body rows to help the eye track across a wide or long table.
   * @polyType Boolean
   * @polyLabel Zebra striping
   * @polyHelp Shades alternating body rows so the eye can track across a wide or long table. Purely visual — it changes no content and no column layout.
   */
  zebraStripes?: boolean;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const COLUMN_INDEXES: readonly ColumnIndex[] = [1, 2, 3, 4, 5, 6];

function resolveColumn(props: ContentTableColumnProps, index: ColumnIndex): ResolvedColumn | null {
  const header = props[`column${index}Header`];
  const cells = props[`column${index}`] ?? [];
  if (!header && cells.length === 0) return null;
  return { index, header, cells };
}

export const ContentTable: React.FC<ContentTableProps> = ({
  title,
  description,
  rowHeaders,
  rowHeadersLabel,
  topNote,
  bottomNote,
  stickyHeader,
  zebraStripes,
  anchorId,
  ...columnProps
}) => {
  const rows = rowHeaders ?? [];
  const columns = COLUMN_INDEXES.map((index) => resolveColumn(columnProps, index)).filter(
    (column): column is ResolvedColumn => column !== null,
  );

  const hasGrid = columns.length > 0 || rows.length > 0;
  const hasRowHeaders = rows.length > 0;
  const rowCount = hasGrid
    ? Math.max(rows.length, ...columns.map((column) => column.cells.length))
    : 0;
  const unnamedColumnList = columns
    .filter((column) => !column.header)
    .map((column) => column.index)
    .join(', ');
  const unlabelledRowCount = hasRowHeaders ? Math.max(0, rowCount - rows.length) : 0;

  const titleId = useId();

  // Reported from an effect rather than the render body because `ListFieldInput` commits one row
  // label per token: a render-body warning fires at every intermediate count while the author is
  // still filling the list, drowning the real misalignment it exists to surface.
  useEffect(() => {
    if (typeof process === 'undefined' || process.env?.NODE_ENV === 'production') return;

    if (unnamedColumnList) {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn(
        `[ContentTable] Column ${unnamedColumnList} has cells but no heading. Add one so screen readers can associate the cells with a column.`,
      );
    }

    if (unlabelledRowCount > 0) {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn(
        `[ContentTable] ${rows.length} row label(s) for ${rowCount} rows. Labels pair with cells by position, so the last ${unlabelledRowCount} row(s) render unlabelled and any intended gap has shifted the labels below it. Add a blank entry for an unlabelled row instead of skipping it.`,
      );
    }
  }, [unnamedColumnList, unlabelledRowCount, rowCount, rows.length]);

  if (!hasGrid && !title && !description && !topNote && !bottomNote) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn('[ContentTable] Nothing authored yet. Skipping render.');
    }
    // The marker carries no block class, so an unauthored block contributes no vertical space.
    // `data-poly-block` is what attributes the hit: `Table` emits the same error value, and an
    // attribute carries no styling where a class would give that space back.
    return (
      <section
        data-poly-error="empty-table"
        data-poly-block="ContentTable"
        aria-hidden="true"
        hidden
      />
    );
  }

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className="content-table"
      data-variant={zebraStripes ? 'zebra' : undefined}
      aria-labelledby={title ? titleId : undefined}
    >
      <div className="content-table__inner">
        {(title || description) && (
          <header className="content-table__header">
            {title && (
              <h2 id={titleId} className="content-table__title">
                {title}
              </h2>
            )}
            {description && (
              <p className="content-table__description">
                <CellText allowedElements={NOTE_ALLOWED_ELEMENTS}>{description}</CellText>
              </p>
            )}
          </header>
        )}

        {/* Framing notes sit outside the scroll container so a pinned header is the only thing
            at its top edge, and so a caveat about the whole table stays visible while it scrolls. */}
        {topNote && (
          <p className="content-table__note content-table__note--top">
            <CellText allowedElements={NOTE_ALLOWED_ELEMENTS}>{topNote}</CellText>
          </p>
        )}

        {hasGrid && (
          <div
            className="content-table__scroll-wrap"
            data-sticky-header={stickyHeader || undefined}
            // The container scrolls on both axes, so it must be focusable to be operable without
            // a mouse: a plain schedule holds no link, so nothing inside it can take focus and
            // the off-screen columns and rows are otherwise unreachable from the keyboard.
            tabIndex={0}
          >
            <table
              className="content-table__table"
              aria-labelledby={title ? titleId : undefined}
            >
              <thead>
                <tr>
                  {hasRowHeaders &&
                    (rowHeadersLabel ? (
                      <th scope="col">{rowHeadersLabel}</th>
                    ) : (
                      // The corner cell labels nothing, so it is not a header. An empty
                      // `th scope="col"` gives the row-label column a blank accessible name and
                      // trips axe's empty-table-header rule; a plain cell holds the grid
                      // position without claiming header semantics.
                      <td />
                    ))}
                  {columns.map((column, columnIndex) => (
                    <th
                      key={columnIndex}
                      scope="col"
                      // A column with cells but no authored heading still renders — dropping it
                      // would silently discard content — so it falls back to a positional
                      // accessible name rather than leaving the scope="col" cell unnamed.
                      aria-label={column.header ? undefined : `Column ${column.index}`}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }, (_, rowIndex) => (
                  <tr key={rowIndex}>
                    {hasRowHeaders &&
                      (rows[rowIndex]?.trim() ? (
                        <th scope="row">
                          <CellText>{rows[rowIndex]}</CellText>
                        </th>
                      ) : (
                        // An unlabelled row — padded, or a deliberately blank label — cannot
                        // carry a row header: an empty `th scope="row"` names every data cell
                        // in the row with a blank string, which reads worse than no association.
                        <td />
                      ))}
                    {columns.map((column, columnIndex) => (
                      <td key={columnIndex}>
                        <CellText>{column.cells[rowIndex]}</CellText>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {bottomNote && (
          <p className="content-table__note content-table__note--bottom">
            <CellText allowedElements={NOTE_ALLOWED_ELEMENTS}>{bottomNote}</CellText>
          </p>
        )}
      </div>
    </section>
  );
};
